// ES module. Loaded via <script type="module"> from main-product.liquid
// and featured-product.liquid (PDP only — not on every page).
//
// Pre-Tier-H this was a classic script that referenced bare `debounce` and
// `window.pauseAllMedia`. Both broke after main.js / secondary.js became
// modules — module top-level identifiers aren't on globalThis and
// `window.pauseAllMedia` was never (re)assigned. This file imports them
// directly now.

import { Component } from "@theme/component";
import { debounce, pauseAllMedia } from "@theme/main";

if (!customElements.get("media-gallery")) {
  class MediaGallery extends Component {
    #controller = new AbortController();

    connectedCallback() {
      super.connectedCallback();

      this.mql = window.matchMedia("(min-width: 750px)");
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend !== "true";
      if (this.productInfo && window.Shopify?.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;

      if (!this.refs.thumbnails) return;

      const { signal } = this.#controller;

      this.refs.viewer.addEventListener(
        "slideChanged",
        debounce(this.onSlideChanged.bind(this), 500),
        { signal }
      );

      // Thumbnail buttons live two levels under the thumbnail slider — a `<li
      // data-target="…">` wrapper with a `<button>` child. Bind the click on
      // each button, signal-tied so reconnect-cycles don't stack listeners.
      this.refs.thumbnails.querySelectorAll("[data-target]").forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector("button");
        if (!button) return;
        button.addEventListener(
          "click",
          () => this.setActiveMedia(mediaToSwitch.dataset.target, false),
          { signal }
        );
      });

      if (this.dataset.desktopLayout.includes("thumbnail") && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#controller.abort();
    }

    onSlideChanged(event) {
      const thumbnail = this.refs.thumbnails?.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );
      this.setActiveThumbnail(thumbnail);
    }

    // Public API — called externally from main.js:3204 (variant change) and
    // main.js:3796 (ProductInfo variant change with filtering). Do not rename.
    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll(
          ".product__media-item, .thumbnail-list__item, .slider-counter__link"
        );
        allSlides.forEach((slide) => {
          if (
            slide.dataset.alt === currentVariant[this.filteringOption] ||
            slide.dataset.alt === "always_display"
          ) {
            slide.classList.remove("hidden");
          } else {
            slide.classList.add("hidden");
          }
        });
      }

      const activeMedia = this.refs.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      this.refs.viewer.querySelectorAll("[data-media-id]").forEach((element) => {
        element.classList.remove("is-active");
      });
      activeMedia.classList.add("is-active");

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.refs.thumbnails) {
          const activeThumbnail = this.refs.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.refs.viewer.slider) this.refs.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.refs.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.refs.viewer && this.refs.viewer.initPages) this.refs.viewer.initPages();
        if (this.refs.viewer && this.refs.viewer.update) this.refs.viewer.update();
      }

      if (!this.refs.thumbnails) return;
      const activeThumbnail = this.refs.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.refs.thumbnails || !thumbnail) return;

      this.refs.thumbnails
        .querySelectorAll("button")
        .forEach((element) => element.removeAttribute("aria-current"));
      thumbnail.querySelector("button").setAttribute("aria-current", true);
      if (this.refs.thumbnails.isSlideVisible(thumbnail, false, 10)) return;

      this.refs.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      if (!this.refs.liveRegion) return;
      const image = activeItem.querySelector(".product__modal-opener--image img");
      if (!image) return;
      image.onload = () => {
        this.refs.liveRegion.setAttribute("aria-hidden", false);
        this.refs.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          "[index]",
          position
        );
        setTimeout(() => {
          this.refs.liveRegion.setAttribute("aria-hidden", true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      pauseAllMedia();
      const deferredMedia = activeItem.querySelector(".deferred-media");
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector("sticky-header");
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
    }

    removeListSemantic() {
      if (!this.refs.viewer.slider) return;
      this.refs.viewer.slider.setAttribute("role", "presentation");
      this.refs.viewer.sliderItems.forEach((slide) => slide.setAttribute("role", "presentation"));
    }
  }

  customElements.define("media-gallery", MediaGallery);
}
